<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { Download } from 'lucide-svelte';
  import { quotationsApi } from '$lib/api/quotations';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';

  const { quotationId, onClose }: { quotationId: number; onClose: () => void } = $props();

  const query = createQuery(() => ({
    queryKey: ['quotation-versions', quotationId],
    queryFn: () => quotationsApi.listVersions(quotationId)
  }));

  let downloadingVersion = $state<number | null>(null);
  async function download(versionNumber: number, formattedVersion: string) {
    downloadingVersion = versionNumber;
    try {
      await quotationsApi.downloadVersionPdf(quotationId, versionNumber, formattedVersion);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      downloadingVersion = null;
    }
  }
</script>

{#if query.isLoading}
  <p class="tym-loading-state">Loading version history&hellip;</p>
{:else if query.isError}
  <p class="tym-error-state">{getErrorMessage(query.error)}</p>
{:else if !query.data?.results.length}
  <div class="py-6 text-center text-dark-gray">
    <p class="text-[13.5px]">No versions created yet.</p>
    <p class="mt-1 text-xs opacity-60">Versions are created when a quotation is approved.</p>
  </div>
{:else}
  <div class="flex flex-col gap-3">
    {#each query.data.results as v (v.id)}
      <div class="rounded-xl border border-toyama-cloudy-gray p-3.5">
        <div class="flex items-start justify-between gap-3">
          <div class="flex items-start gap-3 min-w-0">
            <div
              class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--tym-navy-800)] text-xs font-bold text-white"
            >
              v{v.version_number}
            </div>
            <div class="min-w-0">
              <div class="font-heading text-[13.5px] font-semibold text-navy">{v.formatted_version}</div>
              <div class="mt-0.5 text-xs text-dark-gray">
                {new Date(v.created_at).toLocaleString(undefined, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                })}
              </div>
              {#if v.change_summary}
                <div class="mt-1.5 text-[12.5px] text-toyama-heavy-carbon">
                  <span class="font-medium">Change:</span> {v.change_summary}
                </div>
              {/if}
              {#if v.metadata}
                <div class="mt-1.5 text-xs text-dark-gray">
                  {v.metadata.created_by || 'Unknown'} &middot; {v.metadata.sections_count} sections &middot; {v.metadata.line_items_count} items
                </div>
              {/if}
            </div>
          </div>
          {#if v.pdf_available}
            <button
              type="button"
              class="btn-secondary !py-1.5 !px-2.5 shrink-0 text-[12.5px]"
              disabled={downloadingVersion === v.version_number}
              onclick={() => download(v.version_number, v.formatted_version)}
            >
              <Download class="h-[13px] w-[13px]" /> PDF
            </button>
          {:else}
            <span class="shrink-0 text-xs italic text-dark-gray opacity-40">No PDF</span>
          {/if}
        </div>
      </div>
    {/each}
  </div>
{/if}
